/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { Avatar, Button, Dialog, Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import ConduitSvg from 'bundles/partners-graphql/components/cds/PartnerHero/ConduitSvg';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  hero: css`
    background-color: var(--cds-color-emphasis-primary-background-weak);
    overflow: hidden;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) 0;
    }
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 300px;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: flex-start;
      height: auto;
    }
  `,
  content: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    max-width: 600px;
  `,
  title: css`
    color: var(--cds-color-neutral-primary);
  `,
  description: css`
    color: var(--cds-color-neutral-primary);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;

    ${breakpoints.down('md')} {
      -webkit-line-clamp: 4;
    }

    ${breakpoints.down('sm')} {
      -webkit-line-clamp: 5;
    }
  `,
  readMore: css`
    align-self: flex-start;
  `,
  dialog: css`
    .cds-Dialog-dialog {
      min-height: 200px;
    }
  `,
  dialogContent: css`
    padding: var(--cds-spacing-200);
  `,
  rightSide: css`
    position: relative;
    flex-shrink: 0;
    width: 500px;
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  conduit: css`
    position: absolute;
    top: 0;
    width: 600px;
    right: -50px;
  `,
  avatarDesktopBorder: css`
    position: absolute;
    z-index: 1;
    padding: var(--cds-spacing-50);
    border-radius: var(--cds-border-radius-200);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  avatarDesktopWrapper: css`
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;

    .cds-Avatar-showBorder {
      border-radius: var(
        --cds-border-radius-200
      ) !important; /* radius of avatar doesn't align with cds-radius tokens */
    }
  `,
  avatarMobileWrapper: css`
    display: none;

    ${breakpoints.down('sm')} {
      display: block;
      width: fit-content;
      background-color: var(--cds-color-neutral-background-primary);
      border-radius: var(--cds-border-radius-100);
      overflow: hidden;
      margin-bottom: var(--cds-spacing-100);
    }
  `,
};

type Props = {
  name: string;
  description?: string | null;
  squareLogo?: string | null;
};

const PartnerHero = ({ name, description, squareLogo }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <div css={styles.hero}>
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.row}>
            <div css={styles.content}>
              {squareLogo && (
                <div css={styles.avatarMobileWrapper}>
                  <Avatar variant="organization" size={36} showBorder imgProps={{ src: squareLogo, alt: name }} />
                </div>
              )}
              <Typography2 component="h1" variant="titleLargeCompact" css={styles.title} data-testid="partner-header">
                {name}
              </Typography2>
              {description && (
                <Typography2 component="p" variant="bodyPrimary" css={styles.description}>
                  {description}
                </Typography2>
              )}
              {description && (
                <Button
                  variant="ghostSecondary"
                  size="small"
                  edgeAlign="start"
                  css={styles.readMore}
                  onClick={() => setIsDialogOpen(true)}
                >
                  {_t('Read more')}
                </Button>
              )}
            </div>

            <div css={styles.rightSide}>
              <ConduitSvg css={styles.conduit} />
              {squareLogo && (
                <div css={styles.avatarDesktopBorder}>
                  <div css={styles.avatarDesktopWrapper}>
                    <Avatar variant="organization" size={120} showBorder imgProps={{ src: squareLogo, alt: name }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Grid>
      </PageGridContainer>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} color="darkBlue" css={styles.dialog}>
        <Dialog.HeadingGroup>{_t('About #{partnerName}', { partnerName: name })}</Dialog.HeadingGroup>
        <Dialog.Content css={styles.dialogContent}>
          <Typography2 component="p" variant="bodyPrimary">
            {description}
          </Typography2>
        </Dialog.Content>
      </Dialog>
    </div>
  );
};

export default PartnerHero;
